import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

/**
 * Fixed header shell. Owns the single scroll listener (data-scrolled) used by
 * the announcement bar + navbar shadow. Measures height into --header-height.
 *
 * Mode 1 (data-scrolled=false): full-width strip at top:0, navbar separate below.
 * Mode 2 (data-scrolled=true): connected floating chrome (shared width + shadow).
 * Reserved height stays constant so page content does not jump.
 */
export default function SiteHeader({ children }) {
    const [scrolled, setScrolled] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        const onScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            setScrolled(scrollTop > 0);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        const el = rootRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;

        const publishHeight = () => {
            const height = Math.ceil(el.getBoundingClientRect().height);
            document.documentElement.style.setProperty('--header-height', `${height}px`);
        };

        publishHeight();
        const ro = new ResizeObserver(() => publishHeight());
        ro.observe(el);
        return () => {
            ro.disconnect();
            document.documentElement.style.removeProperty('--header-height');
        };
    }, []);

    return (
        <Box
            ref={rootRef}
            component="header"
            className="site-header"
            data-scrolled={scrolled ? 'true' : 'false'}
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                width: '100%',
                zIndex: 1000,
                display: 'flex',
                justifyContent: 'center',
                boxSizing: 'border-box',
                /* Mode 2 lifts the connected chrome by --header-top; mode 1 uses
                   the same offset as margin between strip and navbar instead. */
                pt: scrolled ? 'var(--header-top)' : 0,
                transition: (theme) =>
                    theme.transitions.create(['padding-top'], {
                        duration: 250,
                        easing: theme.transitions.easing.easeInOut,
                    }),
                '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                },
            }}
        >
            <Box
                className="site-header-chrome"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    width: scrolled ? 'var(--header-width)' : '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    borderRadius: scrolled ? 'var(--header-radius)' : 0,
                    boxShadow: scrolled ? 'var(--header-shadow)' : 'none',
                    overflow: 'visible',
                    transition: (theme) =>
                        theme.transitions.create(['width', 'border-radius', 'box-shadow'], {
                            duration: 250,
                            easing: theme.transitions.easing.easeInOut,
                        }),
                    '@media (prefers-reduced-motion: reduce)': {
                        transition: 'none',
                    },
                    /* Announcement strip: square when detached, top-rounded when connected */
                    '& [data-announcement-bar]': {
                        borderRadius: scrolled
                            ? 'var(--header-radius) var(--header-radius) 0 0'
                            : 0,
                        transition: (theme) =>
                            theme.transitions.create(['border-radius'], {
                                duration: 250,
                                easing: theme.transitions.easing.easeInOut,
                            }),
                        '@media (prefers-reduced-motion: reduce)': {
                            transition: 'none',
                        },
                    },
                    /* Navbar: separate floating pill when not scrolled; flush when scrolled */
                    '& .MuiAppBar-root': {
                        width: scrolled ? '100%' : 'var(--header-width)',
                        maxWidth: '100%',
                        mx: 'auto',
                        mt: scrolled ? 0 : 'var(--header-top)',
                        transition: (theme) =>
                            theme.transitions.create(['width', 'margin-top', 'border-radius'], {
                                duration: 250,
                                easing: theme.transitions.easing.easeInOut,
                            }),
                        '@media (prefers-reduced-motion: reduce)': {
                            transition: 'none',
                        },
                    },
                }}
            >
                {children}
            </Box>
        </Box>
    );
}
